import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Target, Heart, Award, Users } from 'lucide-react';
import schoolVideo from "../assets/SchoolVideo.mp4";

gsap.registerPlugin(ScrollTrigger);

const About = () => {
    useEffect(() => {
        const sections = document.querySelectorAll('.animate-section');
        sections.forEach((section) => {
            gsap.fromTo(
                section,
                { opacity: 0, y: 50 },
                {
                    opacity: 1,
                    y: 0,
                    duration: 1,
                    scrollTrigger: {
                        trigger: section,
                        start: 'top 80%',
                        toggleActions: 'play none none reverse',
                    },
                }
            );
        });
    }, []);

    const values = [
        {
            icon: Target,
            title: 'Our Mission',
            description: 'To provide holistic education that nurtures academic excellence, character development, and prepares students for a dynamic future.',
        },
        {
            icon: Heart,
            title: 'Our Vision',
            description: 'To be a leading educational institution recognized for fostering innovation, critical thinking, and compassionate global citizens.',
        },
        {
            icon: Award,
            title: 'Excellence',
            description: 'We strive for excellence in all aspects of education, from academics to sports and extracurricular activities.',
        },
        {
            icon: Users,
            title: 'Community',
            description: 'Building a strong school community where students, teachers, and parents work together for student success.',
        },
    ];

    const achievements = [
        { year: '2024', title: '100% Pass Rate in Board Exams' },
        { year: '2023', title: 'State Level Sports Championship Winners' },
        { year: '2023', title: 'Best School Award by Education Board' },
        { year: '2022', title: 'Excellence in Science & Mathematics' },
        { year: '2022', title: 'Cultural Activities State Champions' },
        { year: '2021', title: 'Digital Learning Excellence Award' },
    ];

    return (
        <div className="min-h-screen pt-24" data-testid="about-page">
            {/* Hero Section */}
            <section
                className="relative py-20 overflow-hidden"
                style={{
                    backgroundImage: `url(https://images.unsplash.com/photo-1764943630631-b63aadf86e19?w=1920)`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                }}
            >
                <div className="absolute inset-0 bg-gradient-to-br from-violet-900/90 via-purple-800/90 to-blue-900/90"></div>
                <div className="relative z-10 container mx-auto px-4 md:px-8 text-center text-white">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                    >
                        <h1 className="font-outfit font-bold text-5xl md:text-7xl mb-6 text-white">About Us</h1>
                        <p className="font-dm-sans text-xl md:text-2xl max-w-3xl mx-auto text-white/95">
                            Where Excellence Meets Education - Shaping Future Leaders Since 2014
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* Our Story */}
            <section className="py-24 bg-white animate-section">
                <div className="container mx-auto px-4 md:px-8">
                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <div>
                            <h2 className="font-outfit font-bold text-4xl md:text-5xl text-primary mb-6">Our Story</h2>
                            <p className="font-dm-sans text-base md:text-lg text-slate-600 leading-relaxed mb-6">
                                Established in 2014, R.K. English High School has been a cornerstone of quality education in Nallasopara East.
                                Managed by Gonukkad, our institution was founded with a vision to provide accessible, world-class education to students from diverse backgrounds.
                            </p>
                            <p className="font-dm-sans text-base md:text-lg text-slate-600 leading-relaxed mb-6">
                                Over the years, we have grown from a modest beginning to become one of the most trusted educational institutions in the region.
                                Our journey has been marked by continuous innovation, dedicated teaching, and an unwavering commitment to student success.
                            </p>
                            <p className="font-dm-sans text-base md:text-lg text-slate-600 leading-relaxed">
                                Today, we serve over 3600+ students from Kindergarten to Junior College, offering comprehensive programs in Science,
                                Commerce, and Arts. Our modern infrastructure, experienced faculty, and student-centric approach make us the preferred
                                choice for parents seeking quality education.
                            </p>
                        </div>
                        <div className="rounded-3xl overflow-hidden shadow-2xl">
                            <video
                                src={schoolVideo}
                                className="w-full h-full object-cover"
                                autoPlay
                                muted
                                loop
                                playsInline
                                controls
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* Mission & Vision */}
            <section className="py-24 bg-red-700 animate-section">
                <div className="container mx-auto px-4 md:px-8">
                    <div className="text-center mb-16">
                        <h2 className="font-outfit font-bold text-4xl md:text-5xl text-primary mb-4">Our Values</h2>
                        <p className="font-dm-sans text-lg text-slate-600 max-w-2xl mx-auto">
                            The principles that guide our educational philosophy and approach
                        </p>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {values.map((value, index) => (
                            <motion.div
                                key={index}
                                className="bg-white rounded-3xl p-8 shadow-sm hover:shadow-xl transition-all duration-300 border border-slate-100"
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                transition={{ delay: index * 0.1 }}
                                data-testid={`value-card-${index}`}
                            >
                                <value.icon className="w-12 h-12 text-primary mb-4" />
                                <h3 className="font-outfit font-semibold text-2xl mb-3">{value.title}</h3>
                                <p className="font-dm-sans text-slate-600 leading-relaxed">{value.description}</p>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Leadership Messages */}
            <section className="py-24 bg-white animate-section">
                <div className="container mx-auto px-4 md:px-8">
                    <div className="text-center mb-16">
                        <h2 className="font-outfit font-bold text-4xl md:text-5xl text-red mb-4">
                            Leadership Messages
                        </h2>
                        <p className="font-dm-sans text-lg text-black max-w-2xl mx-auto">
                            Guidance and vision from the leadership of R.K. English High School
                        </p>
                    </div>

                    {[
                        {
                            title: "Chairman",
                            name: "Chandrashekhar Vishwakarma",
                            image: "images/Chandrashekhar.jpeg",
                            message: `Dear Parents and Students,

Welcome to R.K. English High School, where we believe education is not just about knowledge, but about shaping character and nurturing talent.

Our dedicated educators work tirelessly to create an environment where every student can explore their potential and develop the skills needed for tomorrow's world.

We combine traditional values with modern teaching methodologies to ensure complete development of our students.

Warm regards,`
                        },
                        {
                            title: "Secretary",
                            name: "Rishikesh Sharma",
                            image: "images/Rishikesh1.jpeg",
                            message: `Education is the foundation of a strong society. At R.K. English High School, we are committed to creating opportunities for students to learn, grow, and succeed.

Our focus is to provide quality education along with strong moral values and discipline so that students become responsible citizens of tomorrow.

I sincerely thank parents and teachers for their continued trust and support.`
                        },
                        {
                            title: "Treasurer",
                            name: "Premlata Vishwakarma",
                            image: "images/Premlata.jpeg",
                            message: `At R.K. English High School, we believe that every child deserves access to quality education and opportunities to excel.

Our institution continuously invests in modern infrastructure, resources, and academic programs to ensure students receive the best learning experience.

Together, we aim to build a brighter future for every student.`
                        }
                    ].map((leader, index) => (
                        <div key={index} className="max-w-5xl mx-auto mb-16">
                            <div className="glass rounded-3xl p-8 md:p-12">
                                <div className="flex flex-col md:flex-row gap-8 items-start">

                                    <div className="shrink-0">
                                        <img
                                            src={leader.image}
                                            alt={leader.title}
                                            className="w-64 h-100 rounded-2xl object-container shadow-xl"
                                        />
                                    </div>

                                    <div>
                                        <h2 className="font-outfit font-bold text-3xl md:text-4xl text-primary mb-2">
                                            {leader.title}'s Message
                                        </h2>

                                        <p className="font-dm-sans text-lg text-slate-600 font-semibold mb-6">
                                            {leader.name}
                                        </p>

                                        <div className="font-dm-sans text-base md:text-lg text-slate-600 leading-relaxed space-y-4">
                                            {leader.message.split("\n").map((line, i) => (
                                                <p key={i}>{line}</p>
                                            ))}
                                        </div>
                                    </div>

                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Achievements */}
            <section className="py-24 bg-gradient-to-br from-violet-900 via-purple-800 to-blue-900 text-white animate-section">
                <div className="container mx-auto px-4 md:px-8">
                    <div className="text-center mb-16">
                        <h2 className="font-outfit font-bold text-4xl md:text-5xl mb-4 text-white">Our Achievements</h2>
                        <p className="font-dm-sans text-xl max-w-2xl mx-auto text-white/90">
                            A legacy of excellence and continuous improvement
                        </p>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {achievements.map((achievement, index) => (
                            <motion.div
                                key={index}
                                className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 hover:bg-white/20 transition-all"
                                initial={{ opacity: 0, scale: 0.9 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                transition={{ delay: index * 0.1 }}
                                data-testid={`achievement-card-${index}`}
                            >
                                <div className="font-outfit font-bold text-secondary text-xl mb-2">{achievement.year}</div>
                                <div className="font-dm-sans text-lg">{achievement.title}</div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Quick Facts */}
            <section className="py-24 bg-white animate-section">
                <div className="container mx-auto px-4 md:px-8">
                    <div className="text-center mb-16">
                        <h2 className="font-outfit font-bold text-4xl md:text-5xl mb-4 text-black">Quick Facts</h2>
                    </div>
                    <div className="max-w-4xl mx-auto">
                        <div className="grid md:grid-cols-2 gap-6">
                            <div className="bg-red-600 rounded-2xl p-6">
                                <h3 className="font-outfit font-bold text-xl text-primary mb-2">Established</h3>
                                <p className="font-dm-sans text-white">2014</p>
                            </div>
                            <div className="bg-red-600 rounded-2xl p-6">
                                <h3 className="font-outfit font-bold text-xl text-primary mb-2">Medium of Instruction</h3>
                                <p className="font-dm-sans text-white">English</p>
                            </div>
                            <div className="bg-red-600 rounded-2xl p-6">
                                <h3 className="font-outfit font-bold text-xl text-primary mb-2">Classes Offered</h3>
                                <p className="font-dm-sans text-white">K.G. to 12th (Science, Commerce, Arts)</p>
                            </div>
                            <div className="bg-red-600 rounded-2xl p-6">
                                <h3 className="font-outfit font-bold text-xl text-primary mb-2">Location</h3>
                                <p className="font-dm-sans text-white">Nallasopara East, Maharashtra</p>
                            </div>
                            <div className="bg-red-600 rounded-2xl p-6">
                                <h3 className="font-outfit font-bold text-xl text-primary mb-2">Total Students</h3>
                                <p className="font-dm-sans text-white">3600+</p>
                            </div>
                            <div className="bg-red-600 rounded-2xl p-6">
                                <h3 className="font-outfit font-bold text-xl text-primary mb-2">Faculty Members</h3>
                                <p className="font-dm-sans text-white">240+</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default About;